/**
 * Offline Indicator - zeigt Online/Offline Status und Sync-Stats in der Header
 */

import { Group, Badge, Tooltip, Text, Loader, Stack, Paper } from '@mantine/core';
import { IconWifi, IconWifiOff, IconCloud, IconAlertCircle } from '@tabler/icons-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { SyncQueuePopover } from './SyncQueuePopover';
import styles from './OfflineIndicator.module.css';

interface OfflineIndicatorProps {
  /** Callback wenn Benutzer auf "Sync Now" klickt (optional) */
  onManualSync?: () => void;
}

export function OfflineIndicator({ onManualSync }: OfflineIndicatorProps) {
  const { isOnline, pendingCount, isSyncing, conflictCount } = useOnlineStatus();

  // Online - keine Anzeige nötig wenn alles synced
  if (isOnline && pendingCount === 0) {
    return (
      <Tooltip label="Online - alle Daten synchronisiert">
        <Group gap="xs" className={styles.indicator}>
          <IconWifi size={16} className={styles.iconOnline} />
          <Text size="sm" fw={500} c="green" visibleFrom="sm">
            Online
          </Text>
        </Group>
      </Tooltip>
    );
  }

  // Offline Mode
  if (!isOnline) {
    return (
      <Tooltip
        label={`Offline - ${pendingCount} ausstehend${conflictCount > 0 ? `, ${conflictCount} Konflikte` : ''}`}
      >
        <Group gap="xs" className={styles.indicator}>
          <IconWifiOff size={16} className={styles.iconOffline} />
          <Text size="sm" fw={500} c="orange" visibleFrom="sm">
            Offline
          </Text>
          {pendingCount > 0 && (
            <Badge size="sm" variant="dot" color="orange">
              {pendingCount}
            </Badge>
          )}
        </Group>
      </Tooltip>
    );
  }

  // Konflikte zuerst prüfen: ein konfliktbehaftetes Item zählt auch als "pending",
  // daher würde der Sync-ausstehend-Block den Konflikt-Hinweis sonst verdecken.
  // Konflikte erfordern manuelle Auflösung → höchste Priorität unter den Online-Zuständen.
  if (conflictCount > 0) {
    return (
      <Tooltip label={`${conflictCount} Konflikte erfordern manuelle Resolution`}>
        <Group gap="xs" className={styles.indicator}>
          <IconAlertCircle size={16} className={styles.iconConflict} />
          <Text size="sm" fw={500} c="red" visibleFrom="sm">
            Konflikte
          </Text>
          <Badge size="sm" variant="dot" color="red">
            {conflictCount}
          </Badge>
        </Group>
      </Tooltip>
    );
  }

  // Syncing läuft
  if (isSyncing) {
    return (
      <Tooltip label="Synchronisierung läuft...">
        <Group gap="xs" className={styles.indicator}>
          <Loader size={16} />
          <Text size="sm" fw={500} c="blue" visibleFrom="sm">
            Syncing...
          </Text>
        </Group>
      </Tooltip>
    );
  }

  // Online mit ausstehenden Syncs — der gesamte Bereich öffnet die Detailliste.
  // Vorher war nur das kleine Wolken-Icon klickbar und löste einen stillen Sync aus:
  // Wer auf „Sync ausstehend" klickte, bekam weder Auskunft noch einen Weg zum Bericht.
  if (pendingCount > 0) {
    return (
      <SyncQueuePopover onManualSync={onManualSync}>
        <Tooltip label={`${pendingCount} Bericht(e) noch nicht übertragen — klicken für Details`}>
          <Group gap="xs" className={styles.indicator}>
            <IconCloud size={16} className={styles.iconPending} />
            <Text size="sm" fw={500} c="blue" visibleFrom="sm">
              Sync ausstehend
            </Text>
            <Badge size="sm" variant="dot" color="blue">
              {pendingCount}
            </Badge>
          </Group>
        </Tooltip>
      </SyncQueuePopover>
    );
  }

  return null;
}

/**
 * Expandable Offline Status Panel (z.B. in Drawer)
 */
export function OfflineStatusPanel() {
  const { isOnline, pendingCount, readyForSyncCount, isSyncing, conflictCount } = useOnlineStatus();

  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="md">
        <div>
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              Synchronisierungs-Status
            </Text>
            <Badge color={isOnline ? 'green' : 'orange'} variant="filled">
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
          </Group>

          <Stack gap="xs">
            <>
              {pendingCount > 0 && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Ausstehende Änderungen
                  </Text>
                  <Text size="sm" fw={600}>
                    {pendingCount}
                  </Text>
                </Group>
              )}

              {readyForSyncCount > 0 && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Bereit zum Syncing
                  </Text>
                  <Text size="sm" fw={600} c="blue">
                    {readyForSyncCount}
                  </Text>
                </Group>
              )}

              {isSyncing && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Syncing läuft...
                  </Text>
                  <Loader size="xs" />
                </Group>
              )}

              {conflictCount > 0 && (
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Konflikte
                  </Text>
                  <Badge color="red" variant="dot">
                    {conflictCount}
                  </Badge>
                </Group>
              )}

              {pendingCount === 0 && (
                <Text size="sm" c="green" fw={500}>
                  ✓ Alles synchronisiert
                </Text>
              )}
            </>
          </Stack>
        </div>
      </Stack>
    </Paper>
  );
}
